import { useQuery } from '@vue/apollo-composable'
import { computed, inject, ref, watch } from 'vue'

import { AuthInstance } from '@/auth'
import { ChildCompanyLocation } from '@/generated/graphql'

import GET_CHILD_COMPANIES_WITH_LOCATION from '../queries/getChildCompaniesWithLocation.gql'

export interface Office {
  id: string
  office: string
  state: string
  city: string
  ambassador?: string
}

export interface UseOfficeSelectionOptions {
  initialSelectedOffices?: Office[]
  initialIncludeHeadOffice?: boolean
}

export function useOfficeSelection(options: UseOfficeSelectionOptions = {}) {
  const $auth = inject<AuthInstance>('auth')

  const { result, loading } = useQuery(GET_CHILD_COMPANIES_WITH_LOCATION, {
    parentId: $auth?.user?.companyId,
  })

  const offices = computed<Office[]>(() => {
    const data = result.value?.childCompaniesWithLocation as ChildCompanyLocation[] | undefined
    if (!data) return []
    return data.map((company) => {
      const formatAmbassador = () => {
        const { firstname = '', lastname = '' } = company?.ambassador || {}
        return [firstname, lastname].filter(Boolean).join(' ')
      }
      return {
        id: company.id || '',
        office: company.name || '',
        state: company.state || '',
        city: company.city || '',
        ambassador: formatAmbassador(),
      }
    })
  })

  const selectedOffices = ref<Office[]>(options.initialSelectedOffices || [])
  const selectedStates = ref<string[]>([])
  const selectedCities = ref<string[]>([])
  const includeHeadOffice = ref(options.initialIncludeHeadOffice || false)
  const hasInitialized = ref(false)

  watch(
    () => offices.value,
    (newOffices) => {
      if (!hasInitialized.value && newOffices.length > 0) {
        if (options.initialSelectedOffices === undefined && selectedOffices.value.length === 0) {
          selectedOffices.value = [...newOffices]
        }
        hasInitialized.value = true
      }
    },
    { immediate: true }
  )

  const uniqueStates = computed(() => {
    const states = offices.value.map((office) => office.state).filter(Boolean)
    return [...new Set(states)].sort()
  })

  const availableCities = computed(() => {
    if (selectedStates.value.length === 0) {
      return []
    }
    const cities = offices.value
      .filter((office) => selectedStates.value.includes(office.state))
      .map((office) => office.city)
      .filter(Boolean)
    return [...new Set(cities)].sort()
  })

  const filteredOffices = computed(() => {
    let result = offices.value

    if (selectedStates.value.length > 0) {
      result = result.filter((office) => selectedStates.value.includes(office.state))
    }

    if (selectedCities.value.length > 0) {
      result = result.filter((office) => selectedCities.value.includes(office.city))
    }

    return result
  })

  const toggleState = (state: string) => {
    const index = selectedStates.value.indexOf(state)
    if (index === -1) {
      selectedStates.value.push(state)
    } else {
      selectedStates.value.splice(index, 1)
    }
    if (selectedStates.value.length === 0) {
      selectedCities.value = []
    } else {
      selectedCities.value = selectedCities.value.filter((city) =>
        availableCities.value.includes(city)
      )
    }
  }

  const toggleCity = (city: string) => {
    const index = selectedCities.value.indexOf(city)
    if (index === -1) {
      selectedCities.value.push(city)
    } else {
      selectedCities.value.splice(index, 1)
    }
  }

  const clearFilters = () => {
    selectedStates.value = []
    selectedCities.value = []
    includeHeadOffice.value = false
  }

  const selectAllOffices = () => {
    selectedOffices.value = [...offices.value]
  }

  const resetState = () => {
    selectedStates.value = []
    selectedCities.value = []
    if (options.initialSelectedOffices !== undefined) {
      selectedOffices.value = [...options.initialSelectedOffices]
      includeHeadOffice.value = options.initialIncludeHeadOffice || false
    } else {
      selectedOffices.value = [...offices.value]
      includeHeadOffice.value = false
    }
  }

  const setInitialValues = (newOffices: Office[], newIncludeHeadOffice: boolean) => {
    selectedOffices.value = [...newOffices]
    includeHeadOffice.value = newIncludeHeadOffice
  }

  return {
    loading,
    offices,
    filteredOffices,
    uniqueStates,
    availableCities,

    selectedOffices,
    selectedStates,
    selectedCities,
    includeHeadOffice,

    toggleState,
    toggleCity,
    clearFilters,
    resetState,
    selectAllOffices,
    setInitialValues,
  }
}
